import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { settingsService } from "@/services/settingsService";
import type { EmailSettingsUpdateDto, EmailTestRequestDto, IntegrationSettingsUpdateDto } from "@/types/integration.dto";
import type { PlatformSettingsUpdateDto } from "@/types/settings.dto";
import { queryKeys } from "./keys";

export function usePlatformSettings() {
  return useQuery({
    queryKey: queryKeys.settings.platform,
    queryFn: () => settingsService.getPlatformSettings(),
  });
}

export function useUpdatePlatformSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: PlatformSettingsUpdateDto) => settingsService.updatePlatformSettings(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.platform });
    },
  });
}

export function useEmailSettings() {
  return useQuery({
    queryKey: queryKeys.settings.email,
    queryFn: () => settingsService.getEmailSettings(),
  });
}

export function useEmailStats() {
  return useQuery({
    queryKey: queryKeys.settings.emailStats,
    queryFn: () => settingsService.getEmailStats(),
  });
}

export function useUpdateEmailSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: EmailSettingsUpdateDto) => settingsService.updateEmailSettings(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.email });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.emailStats });
    },
  });
}

export function useSendTestEmailMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: EmailTestRequestDto) => settingsService.sendTestEmail(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.emailStats });
    },
  });
}

export function useIntegrationSettings() {
  return useQuery({
    queryKey: queryKeys.settings.integrations,
    queryFn: () => settingsService.getIntegrationSettings(),
  });
}

export function useUpdateIntegrationSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: IntegrationSettingsUpdateDto) => settingsService.updateIntegrationSettings(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.integrations });
    },
  });
}
